<template>
  <v-card
    elevation="0"
    v-if="pages === null || pages.length > 0"
    class="floating-arrows-carousel"
  >
    <span class="caption blue-grey--text text--darken-1 font-weight-medium">
      {{ $t('home.activity_board.top_users.top_users_title') }}
    </span>

    <v-card class="card-border mt-2" outlined v-if="!isLoading">
      <Carousel height="auto">
        <template v-slot:prev="{ on, attrs }">
          <Button
            v-bind="attrs"
            v-on="on"
            fab
            elevation="3"
            icon="mdi-chevron-left"
            size="x-small"
            color="white"
            @click="
              trackHomeNavigate({
                section: 'activityBoard',
                element: 'topUsers',
              })
            "
          />
        </template>
        <template v-slot:next="{ on, attrs }">
          <Button
            v-bind="attrs"
            v-on="on"
            fab
            elevation="2"
            icon="mdi-chevron-right"
            size="x-small"
            color="white"
            @click="
              trackHomeNavigate({
                section: 'activityBoard',
                element: 'topUsers',
              })
            "
          />
        </template>

        <template>
          <v-carousel-item v-for="page in pages" :key="`topUsersPage-${page}`">
            <div class="px-4 mt-4 mb-2">
              <span
                class="caption blue-grey--text text--darken-2 font-weight-medium"
              >
                {{ $t(PAGE_TITLES[page]) }}
              </span>
            </div>

            <v-row no-gutters>
              <v-col v-for="user in users[page]" cols="12" :key="user.id">
                <UserItem :user="user" :type="page" />
              </v-col>
            </v-row>
          </v-carousel-item>
        </template>
      </Carousel>
    </v-card>

    <v-card elevation="0" class="card-border my-2" outlined v-else>
      <v-row class="py-2">
        <v-col v-for="item in 3" cols="12" md="12" :key="`skeleton-${item}`">
          <UserItemSkeleton />
        </v-col>
      </v-row>
    </v-card>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { IMAGE_SIZES } from 'rever-media-sdk'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import Carousel from '@/components/UI/Carousel.vue'
import EmptyStateIcon from '@/assets/images/icon-empty-state-challenges.svg'

import UserItem from './UserItem.vue'
import UserItemSkeleton from './UserItemSkeleton.vue'

import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

const PAGE_MAP = {
  revs: 0,
  routines: 1,
}

const PAGE_TITLES = [
  'home.activity_board.top_users.top_users_by_completed_revs_title',
  'home.activity_board.top_users.top_users_by_executed_routines_title',
]

export default {
  name: 'TopUsers',

  mixins: [mixin, userMixin, trackingMixin],

  components: {
    Button,
    Carousel,
    UserItem,
    UserItemSkeleton,
  },

  data() {
    return {
      EmptyStateIcon,
      IMAGE_SIZES,
      isLoading: true,
      isShowingCachedSnapshot: false,
      fetchTimer: null,
      PAGE_MAP,
      PAGE_TITLES,
      currentPage: PAGE_MAP.revs,
      users: [],
    }
  },

  mounted() {
    this.hydrateFromSnapshot()
    this.fetchTimer = setTimeout(
      this.fetchTopUsers,
      HOME_WIDGET_FETCH_DELAYS.activityBoard
    )
  },

  beforeDestroy() {
    clearTimeout(this.fetchTimer)
  },

  computed: {
    ...mapGetters(['accountId', 'organizationId']),

    pages() {
      if (this.isLoading) return null
      return this.users
        .map((user, idx) => {
          if (user.length > 0) return idx
        })
        .filter(page => page !== undefined)
    },
  },

  methods: {
    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot'](
        'activityBoard'
      )

      if (!_.isArray(snapshot?.topUsers)) return

      this.users = [...snapshot.topUsers]
      this.isLoading = false
      this.isShowingCachedSnapshot = true
    },

    saveSnapshot() {
      this.$store.commit('homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        topUsers: [...this.users],
      })
    },

    async fetchTopUsers() {
      const today = moment()
      const startDate = today.startOf('month').format('YYYY-MM-DDThh:mm:ssZ')
      const endDate = today.endOf('month').format('YYYY-MM-DDThh:mm:ssZ')

      try {
        if (!this.isShowingCachedSnapshot) this.isLoading = true

        const [usersByRevs, usersByRoutines] = await Promise.all([
          this.fetchTopUsersByCompletedRevs(startDate, endDate),
          this.fetchTopUsersByExecutedRoutines(startDate, endDate),
        ])

        this.users = [usersByRevs, usersByRoutines]

        this.saveSnapshot()
        this.isShowingCachedSnapshot = false
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.isLoading = false
      }
    },

    async fetchTopUsersByCompletedRevs(startDate, endDate) {
      const response = await api.report.getStats(
        this.organizationId,
        'top_users',
        {
          startDate,
          endDate,
          filters: { entity: 'revs' },
          controls: {
            topLimit: 10,
            criteria: 'by_completed',
          },
        }
      )

      return response?.data?.stats ?? []
    },

    async fetchTopUsersByExecutedRoutines(startDate, endDate) {
      const response = await api.report.getStats(
        this.organizationId,
        'top_users',
        {
          startDate,
          endDate,
          filters: { entity: 'routines' },
          controls: {
            topLimit: 10,
            criteria: 'by_executed',
          },
        }
      )

      return response?.data?.stats ?? []
    },
  },
}
</script>
